// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2020 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import Notify from "@adl/App/Components/Notify";
import xGetExpirationDates from "@adl/App/Handlers/InvoicePayment/GetExpirationDates";
import xGetInvoiceAmountSummation from "@adl/App/Handlers/InvoicePayment/GetInvoiceAmountSummation";
import xGetOpenInvoicePayment from "@adl/App/Handlers/InvoicePayment/InvoicePayment";
import xMakePaymentInvoicePayment from "@adl/App/Handlers/InvoicePayment/MakePayment";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getInvoiceAmountSummation, getPaymentMethod } from "@adl/App/Store/Selectors/InvoicePaymentSelectors";
import CreditCardIFrame from "@adl/App/Widgets/InvoicePayment/CreditCardIFrame";
import {
    invoicePaymentStyle,
    invoicePaymentTotalElementStyle,
} from "@adl/App/Widgets/InvoicePayment/InvoicePayment.style";
import { color, getDataLayer, xIsAuthenticated } from "@adl/XDL/Utilities";
import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { InvoiceModel, PaymentMethodDto } from "@insite/client-framework/Types/ApiModels";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { LoadingOverlay, Pagination, Typography } from "@insite/mobius/index";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import sortBy from "lodash/sortBy";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const settings: any = getSettingsCollection(state);
    const session = getSession(state);
    const paymentMethod = getPaymentMethod(state) as PaymentMethodDto;
    const currentCurrencySymbol = state.context.session.currency?.currencySymbol;
    const isAuthenticated = xIsAuthenticated(state);
    const InvoiceAmountSummation = getInvoiceAmountSummation(state);

    return {
        invoicePaymentResponse: state.xInvoicePayment,
        settings,
        cart,
        billTo: getBillToState(state, session.billToId).value,
        pageTitle: getCurrentPage(state).fields.title,
        session,
        paymentMethod,
        paymentMethodName: paymentMethod?.name,
        currentCurrencySymbol,
        isAuthenticated,
        InvoiceAmountSummation,
        // ADL-1317
        invoicePayment: state.xGA.invoicePayment,
        invoicePaymentEventName: state.xGA.invoicePaymentEventName,
        userEmail: session.email,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    xGetOpenInvoicePayment,
    xMakePaymentInvoicePayment,
    loadCurrentCart,
    xGetExpirationDates,
    xGetInvoiceAmountSummation,
};

const style = invoicePaymentStyle;

const TotalElement: FC<{ title: string; value: string; dataTestSelector: string; showBackground?: boolean }> = ({
    title,
    value,
    dataTestSelector,
    showBackground,
}) => {
    const style = invoicePaymentTotalElementStyle;
    return (
        <StyledWrapper data-test-selector={dataTestSelector} {...style.wrapper}>
            <StyledWrapper {...(showBackground ? style.header.wrapperWithBg : style.header.wrapper)}>
                <Typography {...style.header.title}>{title}</Typography>
            </StyledWrapper>
            <StyledWrapper {...(showBackground ? style.content.wrapperWithBg : style.content.wrapper)}>
                <Typography {...style.content.text}>{value}</Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const InvoicePaymentWidget: FC<Props> = ({
    cart,
    paymentMethod,
    loadCurrentCart,
    billTo,
    pageTitle,
    invoicePaymentResponse,
    xMakePaymentInvoicePayment,
    xGetOpenInvoicePayment,
    currentCurrencySymbol,
    isAuthenticated,
    xGetExpirationDates,
    xGetInvoiceAmountSummation,
    InvoiceAmountSummation,
    invoicePayment,
    invoicePaymentEventName,
    userEmail,
}) => {
    const childRef = useRef<any>();

    const [errorApi, setApiError] = useState("");
    const [paymentAmountValues, setPaymentAmountValues] = useState<{ [key: string]: any }>({});
    const [paymentAmountErrors, setPaymentAmountErrors] = useState<{ [key: string]: string }>({});
    const prevPaymentOptions = usePrevious(cart?.paymentOptions);
    const [customPaymentMethods, setCustomPaymentMethods] = useState<PaymentMethodDto[]>([]);
    const [noAmountsEnteredError, setNoAmountsEnteredError] = useState("");
    const [successPaymentMessage, setSuccessPaymentMessage] = useState("");
    const [notes, setNotes] = useState("");
    const [isSubmitPayment, setIsSubmitPayment] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState("0");
    const toasterContext = useContext(ToasterContext);
    const [payAllInvoicesActivated, setPayAllInvoicesActivated] = useState(false);
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        loadOpenInvoicePayment();
        loadCurrentCart({
            additionalExpands: ["invoicePaymentPage"],
        } as any);
        loadExpDates();
        loadInvoiceAmountSummation();
        const invoicePaymentSuccess = window.localStorage.getItem("invoicePaymentSuccess");
        if (invoicePaymentSuccess) {
            localStorage.removeItem("invoicePaymentSuccess");
            toasterContext.addToast({
                body: siteMessage("Custom_Payment_Success_Message"),

                messageType: "success",
            });
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        updateCustomMethod();
    }, [prevPaymentOptions]);

    const updateCustomMethod = () => {
        const customPaymentMethods: any = [];
        if (cart?.paymentOptions) {
            const paymentMethods = cart?.paymentOptions?.paymentMethods || [];
            paymentMethods.forEach(paymentMethod => {
                if (paymentMethod.isCreditCard || paymentMethod.isPaymentProfile) {
                    customPaymentMethods.push(paymentMethod);
                }
            });
            if (
                !customPaymentMethods ||
                !customPaymentMethods.find((p: PaymentMethodDto) => {
                    return p.isCreditCard;
                })
            ) {
                customPaymentMethods.push({
                    name: "Credit Card",
                    description: "Add Credit Card",
                    isCreditCard: true,
                    isECheck: false,
                    isPaymentProfile: false,
                    cardType: null,
                    billingAddress: null,
                    isPaymentProfileExpired: false,
                    tokenScheme: null,
                });
            }
        }
        setCustomPaymentMethods(customPaymentMethods);
    };

    const dispatch = useDispatch();
    const resetForm = () => {
        dispatch({
            type: "Data/InvoicePayment/ChangePaymentMethod",
            paymentMethod: undefined,
        });
        setApiError("");
        setPaymentAmountValues({});
        setPaymentAmountErrors({});
        setCustomPaymentMethods([]);
        updateCustomMethod();
    };

    const loadOpenInvoicePayment = (page = 1) => {
        xGetOpenInvoicePayment({
            page,
            onError: errorMessage => {
                setApiError(errorMessage);
            },
        });
    };

    const loadExpDates = () => {
        xGetExpirationDates({
            onError: errorMessage => {
                setApiError(errorMessage);
            },
        });
    };
    const loadInvoiceAmountSummation = () => {
        xGetInvoiceAmountSummation({
            onError: errorMessage => {
                setApiError(errorMessage);
            },
        });
    };
    const validateNegativeValue = (value: string, shouldBeNegative: boolean) => {
        if (!shouldBeNegative || !value) {
            if ((value.match(new RegExp("-", "g")) || []).length > 0) {
                return false;
            }
            return true;
        }
        // allow 0 value
        if (value === "0") {
            return true;
        }
        // add dash in the first character only
        if (value.charAt(0) !== "-") {
            return false;
        }
        // prevent multiple dash
        if ((value.match(new RegExp("-", "g")) || []).length > 1) {
            return false;
        }
        return true;
    };

    const onChangePaymentMount = (
        event: React.ChangeEvent<HTMLInputElement>,
        invoiceNumber: string,
        balanceDue: number,
    ) => {
        const values = { ...paymentAmountValues };
        const oldValue = paymentAmountValues[invoiceNumber] ?? "";
        const value = event.target.value;

        const regex = /^[0-9.-]*$/;
        const minValue = balanceDue < 0 ? balanceDue : 0;
        const maxValue = balanceDue < 0 ? 0 : balanceDue;
        const allowNegative = validateNegativeValue(value, minValue === balanceDue);

        if (value !== "" && regex.test(value) && allowNegative) {
            if (value.includes(".") && value.split(".")[1].length > 2) {
                event.preventDefault();
                return;
            }
            if (value === "-" || (parseFloat(value) <= maxValue && parseFloat(value) >= minValue)) {
                setPaymentAmountValues({
                    ...paymentAmountValues,
                    [invoiceNumber]: value,
                });

                values[invoiceNumber] = value;
            } else {
                event.preventDefault();
            }
        } else {
            setPaymentAmountValues({
                ...paymentAmountValues,
                [invoiceNumber]: value === "" ? value : oldValue,
            });

            values[invoiceNumber] = value === "" ? value : oldValue;
        }
    };

    const onBlurPaymentMount = (event: React.ChangeEvent<HTMLInputElement>) => {
        // do something here
    };

    const isError = () => {
        for (const [key, value] of Object.entries(paymentAmountErrors)) {
            if (value) {
                return true;
            }
        }
        return false;
    };

    const calculateTotal = () => {
        let total = 0;

        if (isError()) {
            return translate("Please check your values");
        }

        for (const [key, value] of Object.entries(paymentAmountValues)) {
            if (!value || !isNumeric(value.toString())) {
                continue;
            }
            total += Number(value);
        }
        total += Number(paymentAmount);
        return total.toFixed(2);
    };

    const addCurrency = (number: number | null) => {
        if (isError() || (number !== null && Number.isNaN(number))) {
            return translate("Please check your values");
        }
        number = Number(number);
        const value = Number(number.toFixed(2)) || "0.00";
        return currentCurrencySymbol ? currentCurrencySymbol + value : value;
    };

    const isMakePaymentDisabled = () => {
        const invoicePayments = [];
        Object.keys(paymentAmountValues).forEach((key: any) => {
            const number = paymentAmountValues[key] !== "" ? Number(paymentAmountValues[key]) : 0;
            if (!Number.isNaN(number) && number !== 0) {
                invoicePayments.push(paymentAmountValues[key]);
            }
        });
        return invoicePayments.length === 0 && paymentAmount === "0" && !payAllInvoicesActivated;
    };

    // Xng-Change ADL-1317
    const sendMakePaymentTracking = () => {
        const paymentAmmount = payAllInvoicesActivated
            ? Number(InvoiceAmountSummation) + Number(paymentAmount)
            : Number.isNaN(calculateTotal() as any)
            ? calculateTotal()
            : Number(calculateTotal());
        if (invoicePayment) {
            const dataLayer = getDataLayer();

            if (!dataLayer) {
                return;
            }

            const data = {
                event: invoicePaymentEventName,
                affiliation: billTo?.companyName,
                user: userEmail,
                payment_amount: paymentAmmount,
            };

            dataLayer.push(data);
        }
    };

    // called from CreditCardIFrame component
    const makePaymentSubmit = (data: any) => {
        const InvoicePayments = {} as any;
        Object.keys(paymentAmountValues).forEach((key: any) => {
            const number = paymentAmountValues[key] !== "" ? Number(paymentAmountValues[key]) : 0;
            if (!Number.isNaN(number) && number !== 0) {
                InvoicePayments[key] = paymentAmountValues[key];
            }
        });

        if (isError()) {
            return;
        }
        if (Object.keys(InvoicePayments).length === 0 && !payAllInvoicesActivated) {
            setNoAmountsEnteredError(translate("Please enter payment amounts to submit"));
            return;
        }
        setNoAmountsEnteredError("");

        const parameter = {
            ...data,
            InvoicePayments,
            PayAllInvoices: payAllInvoicesActivated,
        };

        xMakePaymentInvoicePayment({
            parameter,
            onError: (errorMessage: string) => {
                setApiError(errorMessage);
            },
            onSuccess: () => {
                // Xng-Change ADL-1317
                sendMakePaymentTracking();
                resetForm();
                setIsSubmitPayment(true);
                setPayAllInvoicesActivated(false);
                // window.localStorage.setItem("invoicePaymentSuccess", "true");
                // window.location.reload();
            },
            onComplete: () => {
                loadOpenInvoicePayment();
            },
        } as any);
    };

    const formatAmount = (amount = 0) => {
        const newAmount = Number(`${Math.round(Number(`${amount}e2`))}e-2`).toFixed(2);
        return newAmount;
    };

    const makePaymentHandler = () => {
        (childRef.current as any)?.makeCreditCardPayment();
    };

    const payInFullHandler = (invoiceNumber: string, balanceDue: number) => {
        const paymentValues = {
            ...paymentAmountValues,
            [invoiceNumber]: formatAmount(balanceDue),
        };
        setPaymentAmountValues(paymentValues);
        setPaymentAmountErrors({
            ...paymentAmountErrors,
            [invoiceNumber]: "",
        });
    };

    const payAllInvoicesHandler = () => {
        setPayAllInvoicesActivated(true);
        const newState = {};
        setPaymentAmountErrors({});
        setPaymentAmountValues(newState);
    };

    if (!isAuthenticated) {
        return (
            <StyledWrapper data-test-selector="invoicePayment_loading" {...style.notifyMessage.wrapper}>
                <Notify type="danger">{siteMessage("InvoicePayment_SignIn", "You must SignIn")}</Notify>
            </StyledWrapper>
        );
    }

    // Render
    if (!invoicePaymentResponse.invoicePayment && invoicePaymentResponse.isLoading) {
        return (
            <StyledWrapper data-test-selector="invoicePayment_loading" {...style.loading.wrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const paymentAmountChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        const decimalPosition = value.indexOf(".");
        const twoDecimal = decimalPosition !== -1 && value.slice(decimalPosition, decimalPosition + 3);
        value = `${Math.floor(+value)}`;
        if (!Number.isNaN(+value) && +value < 0) {
            value = `${Math.abs(+value)}`;
        }
        if (!value || !Number.isNaN(+value)) {
            setPaymentAmount(`${value}${(twoDecimal) || ""}`);
        } else {
            event.preventDefault();
        }
    };

    const pagination = invoicePaymentResponse.invoicePayment?.pagination;

    const changePage = (newPageIndex: number) => {
        loadOpenInvoicePayment(newPageIndex);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        // const newPageSize = parseInt(event.currentTarget.value, 10);
        // updateSearchFields({
        //     page: 1,
        //     pageSize: newPageSize,
        // });
    };

    return (
        <LoadingOverlay data-test-selector="invoicePayment" loading={invoicePaymentResponse.isLoading}>
            <StyledWrapper>
                <Typography variant="h3" as="h1" data-test-selector="PageTitle" {...style.pageTitle}>
                    {billTo?.customerName ? `${billTo?.customerName} - ` : ""}
                    {pageTitle}
                </Typography>
            </StyledWrapper>
            {!invoicePaymentResponse.isLoading && !invoicePaymentResponse.invoicePayment?.invoices.length && (
                <Notify data-test-selector="invoicePayment_emptyInvoices" type="danger">
                    {siteMessage("InvoicePayment_EmptyInvoices")}
                </Notify>
            )}
            <Notify type="warning" data-test-selector="invoicePayment_payInvoices">
                {siteMessage("Custom_Payment_Submit_Message")}
            </Notify>
            {invoicePaymentResponse.invoicePayment?.invoices.length > 0 && (
                <>
                    <StyledWrapper data-test-selector="invoicePayment_table" {...style.tableWrapper}>
                        <DataTable className="mobile-view">
                            <DataTableHead>
                                <DataTableHeader tight>{translate("Invoice ID")}</DataTableHeader>
                                <DataTableHeader tight>{translate("Purchase Order #")}</DataTableHeader>
                                <DataTableHeader tight>{translate("Invoice Date")}</DataTableHeader>
                                <DataTableHeader tight>{translate("Due Date")}</DataTableHeader>
                                <DataTableHeader tight {...style.shipToCell?.wrapper}>
                                    {translate("Job Name")}
                                </DataTableHeader>
                                <DataTableHeader tight {...style.balanceDueCell}>
                                    {translate("Balance Due")}
                                </DataTableHeader>
                                <DataTableHeader tight {...style.emptyCell}></DataTableHeader>
                                <DataTableHeader tight {...style.paymentAmountCell}>
                                    {translate("Payment Amount")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {sortBy(invoicePaymentResponse.invoicePayment?.invoices, "dueDate").map(
                                    ({
                                        invoiceNumber,
                                        invoiceDate,
                                        dueDate,
                                        stCompanyName,
                                        currentBalance,
                                        currentBalanceDisplay,
                                        isPendingPartialPayment,
                                        customerPO,
                                    }: any) => (
                                        <DataTableRow
                                            key={invoiceNumber}
                                            {...(new Date(dueDate) < new Date() && style.overDueInvoice)}
                                        >
                                            <DataTableCell data-th={`${translate("Invoice Number")}:`}>
                                                <Link
                                                    href={`/MyAccount/Invoices/Details?invoiceNumber=${invoiceNumber}`}
                                                    data-test-selector="invoiceHistory_invoiceLine_number"
                                                    {...style.invoiceId}
                                                >
                                                    {invoiceNumber}
                                                </Link>
                                            </DataTableCell>
                                            <DataTableCell data-th={`${translate("PO #")}:`}>
                                                {customerPO}
                                            </DataTableCell>
                                            <DataTableCell
                                                data-th={`${translate("Invoice Date")}:`}
                                                {...(new Date(dueDate) < new Date() && style.overDueInvoiceDate)}
                                            >
                                                {invoiceDate.split("T")[0] || invoiceDate}
                                            </DataTableCell>
                                            <DataTableCell
                                                data-th={`${translate("Due Date")}:`}
                                                {...(new Date(dueDate) < new Date() && style.overDueInvoiceDate)}
                                            >
                                                {dueDate.split("T")[0] || dueDate}
                                            </DataTableCell>
                                            <DataTableCell
                                                data-th={`${translate("Ship To")}:`}
                                                {...style.shipToCell?.wrapper}
                                            >
                                                {stCompanyName}
                                            </DataTableCell>
                                            <DataTableCell
                                                data-th={`${translate("Balance Due")}:`}
                                                {...style.balanceDueCell}
                                            >
                                                <StyledWrapper {...style.balanceDueWrapper}>
                                                    <Typography>{currentBalanceDisplay}</Typography>
                                                    {isPendingPartialPayment && (
                                                        <Typography>
                                                            {siteMessage("Custom_PendingPartialPayment")}
                                                        </Typography>
                                                    )}
                                                </StyledWrapper>
                                            </DataTableCell>
                                            <DataTableCell {...style.emptyCell} tight></DataTableCell>
                                            <DataTableCell
                                                data-th={`${translate("Payment")}:`}
                                                className="value-cell"
                                                {...style.paymentAmountCell}
                                            >
                                                <TextField
                                                    disabled={payAllInvoicesActivated}
                                                    type="text"
                                                    value={paymentAmountValues[`${invoiceNumber.toString()}`] ?? 0}
                                                    error={paymentAmountErrors[`${invoiceNumber.toString()}`] || ""}
                                                    {...style.textFieldStyle}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                        onChangePaymentMount(event, invoiceNumber, currentBalance)
                                                    }
                                                    onBlur={onBlurPaymentMount}
                                                />
                                                <Button
                                                    disabled={payAllInvoicesActivated}
                                                    variant="secondary"
                                                    sizeVariant="small"
                                                    data-test-selector="pay_in_full"
                                                    css={css`
                                                        width: 100%;
                                                        margin-top: 10px;
                                                        background: ${color("common.white")};
                                                    `}
                                                    onClick={() => payInFullHandler(invoiceNumber, currentBalance)}
                                                >
                                                    {translate("Pay In Full")}
                                                </Button>
                                            </DataTableCell>
                                        </DataTableRow>
                                    ),
                                )}
                            </DataTableBody>
                        </DataTable>
                        <StyledWrapper {...style.paginationWrapper}>
                            <Pagination
                                {...style.pagination}
                                resultsCount={pagination.totalItemCount}
                                currentPage={pagination.page}
                                resultsPerPage={pagination.pageSize}
                                resultsPerPageOptions={pagination.pageSizeOptions}
                                onChangePage={changePage}
                                onChangeResultsPerPage={changeResultsPerPage}
                                pageSizeCookie="InvoicePayment-PageSize"
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...style.noteResultWrapper}>
                        <StyledWrapper {...style.noteField}>
                            <TextArea
                                placeholder={translate("Enter Payment Comments")}
                                label={translate("Payment Comments (Optional)")}
                                value={notes}
                                onChange={changeHandler}
                                data-test-selector="cartline_notes"
                            ></TextArea>
                        </StyledWrapper>
                        <StyledWrapper {...style.resultSection}>
                            <TotalElement
                                dataTestSelector="total_balanceDue"
                                title="Total Balance Due"
                                value={`${
                                    Number.isNaN(InvoiceAmountSummation) || !InvoiceAmountSummation
                                        ? "$0.00"
                                        : addCurrency(Number(InvoiceAmountSummation))
                                }`}
                            />
                            <StyledWrapper {...style.payAllInvoicesWrapper}>
                                <Button
                                    {...style.payAllInvoicesBtn}
                                    data-test-selector="pay_all_invoices"
                                    variant="secondary"
                                    sizeVariant="large"
                                    onClick={payAllInvoicesHandler}
                                >
                                    {translate("Pay All Invoices")}
                                </Button>
                                <TextField
                                    type="text"
                                    label={translate("Additional Payment Amount")}
                                    value={paymentAmount}
                                    onChange={paymentAmountChangeHandler}
                                    {...style.alternatePaymentAmountField}
                                    data-test-selector="invoicesPaymentAmount"
                                ></TextField>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...style.ccPaymentNotifyWrapper} data-test-selector="makePayment_creditCard">
                        <StyledWrapper {...style.ccAndPaymentWrapper}>
                            {customPaymentMethods.length > 0 && (
                                <CreditCardIFrame
                                    ref={childRef}
                                    makePaymentSubmit={makePaymentSubmit}
                                    customPaymentMethods={customPaymentMethods}
                                    paymentComments={notes}
                                    alternatePaymentAmount={paymentAmount}
                                />
                            )}
                            <StyledWrapper className="total_paymentAmount">
                                <TotalElement
                                    dataTestSelector="total_paymentAmount"
                                    title={translate("Payment Amount")}
                                    value={`${
                                        payAllInvoicesActivated
                                            ? addCurrency(Number(InvoiceAmountSummation) + Number(paymentAmount))
                                            : Number.isNaN(calculateTotal() as any)
                                            ? calculateTotal()
                                            : addCurrency(Number(calculateTotal()))
                                    }`}
                                    showBackground={true}
                                />
                                <StyledWrapper data-test-selector="makePayment">
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                        `}
                                    >
                                        <Button
                                            {...style.makePaymentBtn}
                                            disabled={isMakePaymentDisabled()}
                                            data-test-selector="makePayment_submit"
                                            onClick={makePaymentHandler}
                                        >
                                            {translate("Make Payment")}
                                        </Button>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                        {noAmountsEnteredError && <Notify type="danger">{noAmountsEnteredError}</Notify>}
                        {errorApi && (
                            <Notify data-test-selector="invoicePayment_errorApi" type="danger">
                                {errorApi}
                            </Notify>
                        )}
                    </StyledWrapper>
                </>
            )}
        </LoadingOverlay>
    );
};

const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    displayName: "Invoice Payment Widget",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoicePaymentWidget),
    definition,
};

export default widgetModule;
